import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Documents from "@/pages/Documents";
import Messages from "@/pages/Messages";
import Reports from "@/pages/Reports";
import Calendar from "@/pages/Calendar";
import Tasks from "@/pages/Tasks";
import Intakes from "@/pages/Intakes";
import Contacts from "@/pages/Contacts";
import Docket from "@/pages/Docket";
import MedicalProviders from "@/pages/MedicalProviders";
import Calls from "@/pages/Calls";
import DocumentTemplates from "@/pages/DocumentTemplates";
import PublicIntake from "@/pages/PublicIntake";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import SetPassword from "@/pages/SetPassword";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import MicrosoftCallback from "@/pages/MicrosoftCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import IdleMonitor from "@/components/IdleMonitor";
import Layout from "@/components/Layout";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/staff-login" element={<PublicOnly><Login showPassword /></PublicOnly>} />
            <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/public/intake" element={<PublicIntake />} />
            <Route path="/set-password" element={<SetPassword mode="invite" />} />
            <Route path="/reset-password" element={<SetPassword mode="reset" />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
            <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/intake" element={<ProtectedRoute><Intakes /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
            <Route path="/docket" element={<ProtectedRoute><Docket /></ProtectedRoute>} />
            <Route path="/medical" element={<ProtectedRoute><MedicalProviders /></ProtectedRoute>} />
            <Route path="/calls" element={<ProtectedRoute><Calls /></ProtectedRoute>} />
            <Route path="/templates" element={<AdminRoute><DocumentTemplates /></AdminRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/users" element={<AdminRoute><Users /></AdminRoute>} />
            <Route path="/audit" element={<AdminRoute><Audit /></AdminRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <IdleMonitor />
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
